// Upload a new doc (.html or .zip) from the browser. Drag-drop or pick a file,
// optionally retitle, and watch progress. The destination folder is the folder
// currently open in the workspace. On success the parent navigates to the doc.

import { useRef, useState } from "react";
import { Modal } from "./Modal";
import { Spinner } from "./Spinner";
import { Icon } from "./Icon";
import { isUploadable, uploadDoc } from "../upload";
import { useToast } from "./Toast";

interface Props {
  folderId: string | null;
  folderName?: string;
  onClose: () => void;
  onUploaded: (docId: string) => void;
}

export function UploadDialog({ folderId, folderName, onClose, onUploaded }: Props) {
  const [file, setFile] = useState<File | null>(null);
  const [title, setTitle] = useState("");
  const [busy, setBusy] = useState(false);
  const [progress, setProgress] = useState(0);
  const [dragOver, setDragOver] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const inputRef = useRef<HTMLInputElement | null>(null);
  const { success } = useToast();

  const pick = (f: File | null) => {
    setError(null);
    if (!f) return;
    if (!isUploadable(f)) {
      setError("Upload a self-contained .html file or a .zip bundle.");
      return;
    }
    setFile(f);
    if (!title) setTitle(f.name.replace(/\.[^.]+$/, ""));
  };

  const submit = async () => {
    if (!file) return;
    setBusy(true);
    setError(null);
    setProgress(0);
    try {
      const result = await uploadDoc(file, {
        title: title.trim() || undefined,
        folderId,
        onProgress: setProgress,
      });
      success(`Uploaded “${result.title}”.`);
      onUploaded(result.docId);
    } catch (e) {
      setError((e as Error).message);
      setBusy(false);
    }
  };

  return (
    <Modal
      title={folderName ? `Upload to ${folderName}` : "Upload a document"}
      onClose={busy ? () => {} : onClose}
      footer={
        <>
          <button type="button" className="btn btn-ghost" onClick={onClose} disabled={busy}>
            Cancel
          </button>
          <button
            type="button"
            className="btn btn-primary"
            onClick={submit}
            disabled={!file || busy}
          >
            {busy ? <Spinner /> : <Icon name="upload" size={18} />}
            {busy ? "Uploading…" : "Upload"}
          </button>
        </>
      }
    >
      <div
        className={`dropzone${dragOver ? " is-over" : ""}${file ? " has-file" : ""}`}
        onDragOver={(e) => {
          e.preventDefault();
          setDragOver(true);
        }}
        onDragLeave={() => setDragOver(false)}
        onDrop={(e) => {
          e.preventDefault();
          setDragOver(false);
          pick(e.dataTransfer.files?.[0] ?? null);
        }}
        onClick={() => inputRef.current?.click()}
        role="button"
        tabIndex={0}
        onKeyDown={(e) => {
          if (e.key === "Enter" || e.key === " ") inputRef.current?.click();
        }}
      >
        <Icon name={file ? "file" : "upload"} size={28} />
        {file ? (
          <p className="dropzone-text">
            <strong>{file.name}</strong>
            <span className="caption"> · {(file.size / 1024).toFixed(0)} KB</span>
          </p>
        ) : (
          <p className="dropzone-text">
            Drag a <strong>.html</strong> or <strong>.zip</strong> here, or click to
            choose
          </p>
        )}
        <input
          ref={inputRef}
          type="file"
          accept=".html,.htm,.zip"
          hidden
          onChange={(e) => pick(e.target.files?.[0] ?? null)}
        />
      </div>

      {file && (
        <label className="field">
          <span className="field-label">Title</span>
          <input
            className="input"
            value={title}
            onChange={(e) => setTitle(e.target.value)}
            placeholder="Document title"
            disabled={busy}
          />
        </label>
      )}

      {busy && (
        <div className="progress" aria-hidden="true">
          <div className="progress-bar" style={{ width: `${Math.round(progress * 100)}%` }} />
        </div>
      )}

      {error && (
        <div className="error-banner" role="alert">
          {error}
        </div>
      )}
    </Modal>
  );
}
